import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const formatCurrency = (val) => Number(val || 0).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });

function LandlordSettings({ embedded = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [stripeReturnNotice, setStripeReturnNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState({ subscription_status: 'free' });
  const [connectStatus, setConnectStatus] = useState({ connected: false });
  const [payoutInfo, setPayoutInfo] = useState(null);
  const [settings, setSettings] = useState({
    pass_card_fee_to_tenant: true,
    default_platform_fee_pct: 1,
    platform_fee_type: 'percent',
    platform_fee_value: 1,
    pass_through_platform_fee: false,
    pass_through_card_fee: true,
    pass_through_pad_fee: false,
    processing_fee_mode: 'pass_through',
    pad_failure_fee_policy: 'pass_through',
    preauth_enabled: false,
    preauth_days_before: 3,
    preauth_amount_pct: 100,
    late_fee_enabled: false,
    late_fee_type: 'fixed',
    late_fee_amount: 0,
    late_fee_grace_days: 0
  });
  const [saving, setSaving] = useState(false);
  const [generatingLedgers, setGeneratingLedgers] = useState(false);
  const [message, setMessage] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [helpTopic, setHelpTopic] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stripeParam = params.get('stripe');
    if (stripeParam === 'return') {
      setStripeReturnNotice('Stripe setup complete! Your account status will update shortly.');
      // Clean URL
      params.delete('stripe');
      const cleaned = params.toString();
      navigate(location.pathname + (cleaned ? `?${cleaned}` : ''), { replace: true });
    } else if (stripeParam === 'refresh') {
      setStripeReturnNotice('Stripe session expired. Please try connecting again.');
      params.delete('stripe');
      const cleaned = params.toString();
      navigate(location.pathname + (cleaned ? `?${cleaned}` : ''), { replace: true });
    }
  }, [location.search, navigate, location.pathname]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setHelpTopic(null);
    };
    if (helpTopic) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [helpTopic]);

  const fetchAll = async () => {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    if (!token || userType !== 'landlord') {
      navigate('/login');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [subRes, connectRes, settingsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/subscription`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/connect/status`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/payments/settings`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const subData = await subRes.json().catch(() => ({}));
      if (subRes.ok) setSubscription(subData.subscription || { subscription_status: 'free' });

      const connectData = await connectRes.json().catch(() => ({}));
      if (connectRes.ok) {
        setConnectStatus(connectData || { connected: false });
        // Fetch payouts if connected
        if (connectData.connected && connectData.payouts_enabled) {
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/connect/payouts`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(r => r.json()).then(d => { if (d.connected) setPayoutInfo(d); }).catch(() => {});
        }
      }

      const settingsData = await settingsRes.json().catch(() => ({}));
      if (settingsRes.ok && settingsData.settings) {
        setSettings(settingsData.settings);
      }
    } catch (err) {
      setError(err.message || 'Failed to load settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchAll();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/connect/onboard`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Unable to start onboarding.');
      if (data.url) window.location.href = data.url;
    } catch (err) {
      setMessage(err.message || 'Unable to start onboarding.');
    }
  };

  const handleEnablePayments = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/payments/enable`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Unable to enable payments.');
      setMessage('Payments enabled.');
      await fetchAll();
    } catch (err) {
      setMessage(err.message || 'Unable to enable payments.');
    }
  };

  const handleSaveSettings = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setSaving(true);
    setMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/payments/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          pass_card_fee_to_tenant: !!settings.pass_card_fee_to_tenant,
          default_platform_fee_pct: settings.default_platform_fee_pct,
          preauth_enabled: !!settings.preauth_enabled,
          preauth_days_before: settings.preauth_days_before,
          preauth_amount_pct: settings.preauth_amount_pct,
          late_fee_enabled: !!settings.late_fee_enabled,
          late_fee_type: settings.late_fee_type || 'fixed',
          late_fee_amount: Number(settings.late_fee_amount || 0),
          late_fee_grace_days: Number(settings.late_fee_grace_days || 0)
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Unable to save settings.');
      setSettings(data.settings || settings);
      setMessage('Payment settings updated.');
    } catch (err) {
      setMessage(err.message || 'Unable to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateLedgers = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setGeneratingLedgers(true);
    setMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/payments/generate-ledgers`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Unable to generate ledgers.');
      setMessage(`Generated ${data.created || 0} ledger entries for ${data.tenants_processed || 0} tenants.`);
    } catch (err) {
      setMessage(err.message || 'Unable to generate ledgers.');
    } finally {
      setGeneratingLedgers(false);
    }
  };

  const sampleRent = 1000;
  const platformFeeType = settings.platform_fee_type || 'percent';
  const platformFeeValue = settings.platform_fee_value ?? settings.default_platform_fee_pct ?? 1;
  const platformFeeAmount = Math.max(
    0,
    platformFeeType === 'flat'
      ? Number(platformFeeValue || 0)
      : (sampleRent * Number(platformFeeValue || 0)) / 100
  );
  const passThroughPlatformFee = !!settings.pass_through_platform_fee;
  const passThroughCardFee = settings.pass_through_card_fee ?? settings.pass_card_fee_to_tenant ?? true;
  const baseCharge = sampleRent + (passThroughPlatformFee ? platformFeeAmount : 0);
  const rawCardFee = baseCharge * 0.029 + 0.30;
  const cardFeeAmount = passThroughCardFee ? Math.max(0, rawCardFee / (1 - 0.029)) : Math.max(0, rawCardFee);
  const tenantPays = sampleRent + (passThroughPlatformFee ? platformFeeAmount : 0) + (passThroughCardFee ? cardFeeAmount : 0);
  const landlordReceives = tenantPays - platformFeeAmount - cardFeeAmount;

  const subscriptionActive = subscription?.subscription_status === 'active';
  const paymentsReady = subscriptionActive && !!settings.payments_enabled && !!connectStatus.connected && !!connectStatus.charges_enabled && !!connectStatus.payouts_enabled;
  const needsStripeFix = !!connectStatus.connected && (!connectStatus.charges_enabled || !connectStatus.payouts_enabled);
  const step1Complete = subscriptionActive;
  const step2Complete = !!connectStatus.connected && !!connectStatus.charges_enabled && !!connectStatus.payouts_enabled;
  const step3Complete = !!settings.payments_enabled;

  const stepHelpContent = {
    subscribe: {
      title: 'Why subscribe to Pro?',
      points: [
        'Rent collection, Stripe integration, and tenant payments are Pro-only features.',
        'Pro also unlocks maintenance automation, application workflows, and support.',
        'You can cancel anytime—subscriptions are billed monthly in test mode.'
      ]
    },
    connect: {
      title: 'Why connect Stripe?',
      points: [
        'Stripe securely stores bank details and handles payouts to your account.',
        'The Express onboarding takes ~3 minutes and collects your business + banking info.',
        'If you stop halfway, just reopen the flow to resume—nothing is lost.'
      ]
    },
    enable: {
      title: 'Why enable payments in Rentual?',
      points: [
        'This final switch tells Rentual to start creating rent charges in Stripe.',
        'You can toggle it off later if you need to pause collections.',
        'Once enabled, tenants immediately see the “Pay” button in their portal.'
      ]
    }
  };

  const onboardingSteps = [
    {
      id: 'subscribe',
      title: 'Subscribe to Pro',
      description: 'Rent collection is part of the Pro plan. Upgrade to unlock payments tools.',
      help: 'Pro unlocks rent collection, Stripe integration, and advanced support.',
      complete: step1Complete,
      actionLabel: step1Complete ? 'Subscribed' : 'View plans',
      action: () => navigate('/pricing'),
      disabled: step1Complete
    },
    {
      id: 'connect',
      title: 'Connect Stripe',
      description: needsStripeFix
        ? 'Stripe still needs a bit more info. Click below to resume the secure onboarding flow.'
        : 'Securely connect your Stripe Express account so tenants can pay you online.',
      help: 'Stripe collects your business + banking info to enable charges and payouts.',
      complete: step2Complete,
      actionLabel: step2Complete ? 'Connected' : needsStripeFix ? 'Finish Stripe setup' : 'Connect Stripe',
      action: handleConnect,
      disabled: step2Complete
    },
    {
      id: 'enable',
      title: 'Enable payments in Rentual',
      description: 'Turn on Rentual’s rent collection with your connected Stripe account.',
      help: 'Once enabled, tenants immediately see the Pay button and scheduled rent uses your Stripe account.',
      complete: step3Complete,
      actionLabel: step3Complete ? 'Payments enabled' : 'Enable payments',
      action: handleEnablePayments,
      disabled: step3Complete || !step1Complete || !step2Complete
    }
  ];

  const activeHelp = helpTopic ? stepHelpContent[helpTopic] : null;

  const content = (
    <>
      <div className="ledger-header">
        <div>
          <p className="eyebrow-tag">Settings</p>
          <h2>Payments</h2>
          <p>Connect Stripe and configure fee settings.</p>
        </div>
      </div>

      {stripeReturnNotice && (
        <div className="dashboard-section notice-card" style={{ marginBottom: '1rem', borderLeft: '4px solid #00d924', background: 'var(--surface-color, #f0fff4)', position: 'relative' }}>
          <button onClick={() => setStripeReturnNotice('')} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem' }} aria-label="Dismiss">✕</button>
          <p className="status-text" style={{ margin: 0 }}>{stripeReturnNotice}</p>
        </div>
      )}

      {error && <p className="error-message">{error}</p>}
      {message && <p className="status-text">{message}</p>}
      {!paymentsReady && (
        <div className="dashboard-section" style={{ marginTop: '0.75rem', borderLeft: '4px solid #f5a623', background: 'var(--surface-color, #fff7e6)', padding: '0.75rem' }}>
          <p className="status-text" style={{ margin: 0 }}>Connect Stripe and enable payments to collect rent online.</p>
        </div>
      )}
      {loading ? (
        <p className="status-text">Loading payments settings…</p>
      ) : (
        <>
          <section className="dashboard-section" style={{ marginTop: '1rem' }}>
            <div className="section-header" style={{ alignItems: 'center' }}>
              <div>
                <p className="eyebrow-tag">Get ready to collect rent</p>
                <h3>Complete these three steps</h3>
                <p className="status-text">We’ll check each step automatically and let you know when you’re good to go.</p>
              </div>
              <span className={`report-chip ${paymentsReady ? 'report-chip--success' : 'report-chip--muted'}`}>
                {paymentsReady ? 'Ready' : 'Action needed'}
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
              {onboardingSteps.map((step, index) => {
                const stepNumber = index + 1;
                const priorStepsComplete = onboardingSteps.slice(0, index).every((s) => s.complete);
                const isActive = !step.complete && priorStepsComplete;
                const borderColor = step.complete ? '#16a34a' : isActive ? '#2f6fed' : '#e4e4e7';
                const background = step.complete ? 'var(--surface-color, #f0fdf4)' : isActive ? 'var(--surface-color, #f5f8ff)' : 'var(--surface-color, #ffffff)';
                return (
                  <div
                    key={step.id}
                    className="onboarding-step"
                    style={{
                      border: `1px solid ${borderColor}`,
                      borderRadius: '12px',
                      padding: '1rem',
                      background
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1 }}>
                        <p className="eyebrow-tag">Step {stepNumber}</p>
                        <h4 style={{ margin: '0 0 0.25rem 0' }}>{step.title}</h4>
                        <p className="status-text" style={{ margin: 0 }}>{step.description}</p>
                        {step.help && (
                          <button
                            type="button"
                            className="info-chip-button"
                            style={{ marginTop: '0.35rem' }}
                            onClick={() => setHelpTopic(step.id)}
                            title={step.help}
                          >
                            Why this step
                          </button>
                        )}
                        {step.id === 'connect' && connectStatus.connected && (
                          <>
                            <p className="status-text" style={{ marginTop: '0.35rem' }}>
                              Charges enabled: {connectStatus.charges_enabled ? 'Yes' : 'No'} · Payouts enabled: {connectStatus.payouts_enabled ? 'Yes' : 'No'}
                            </p>
                            {payoutInfo && (
                              <div style={{ marginTop: '0.5rem' }}>
                                <p className="status-text" style={{ fontWeight: 600 }}>
                                  Balance: {payoutInfo.balance.available.map(b => `${formatCurrency(b.amount / 100)} ${b.currency.toUpperCase()}`).join(', ') || '$0.00'}
                                  {payoutInfo.balance.pending.length > 0 && (
                                    <span style={{ color: '#6b7280' }}> · Pending: {payoutInfo.balance.pending.map(b => formatCurrency(b.amount / 100)).join(', ')}</span>
                                  )}
                                </p>
                                <p className="status-text">
                                  Payout schedule: {payoutInfo.schedule.interval === 'daily' ? 'Daily' : payoutInfo.schedule.interval === 'weekly' ? `Weekly (${payoutInfo.schedule.weekly_anchor || 'Monday'})` : payoutInfo.schedule.interval === 'monthly' ? `Monthly (day ${payoutInfo.schedule.monthly_anchor || 1})` : payoutInfo.schedule.interval || 'Standard'}
                                  {payoutInfo.schedule.delay_days ? ` · ${payoutInfo.schedule.delay_days} day delay` : ''}
                                </p>
                                {payoutInfo.payouts.length > 0 && (
                                  <details style={{ marginTop: '0.35rem' }}>
                                    <summary className="status-text" style={{ cursor: 'pointer', fontWeight: 600 }}>Recent payouts ({payoutInfo.payouts.length})</summary>
                                    <div style={{ marginTop: '0.25rem' }}>
                                      {payoutInfo.payouts.map(p => (
                                        <p key={p.id} className="status-text" style={{ margin: '0.15rem 0' }}>
                                          {formatCurrency(p.amount / 100)} {p.currency.toUpperCase()} · {p.status} · arrives {new Date(p.arrival_date * 1000).toLocaleDateString()}
                                        </p>
                                      ))}
                                    </div>
                                  </details>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <div style={{ minWidth: '200px', textAlign: 'right' }}>
                        <span className={`report-chip ${step.complete ? 'report-chip--success' : 'report-chip--muted'}`}>
                          {step.complete ? 'Complete' : 'Pending'}
                        </span>
                        <div className="form-actions" style={{ marginTop: '0.5rem', justifyContent: 'flex-end' }}>
                          <button
                            className="secondary-button"
                            onClick={step.action}
                            disabled={step.disabled}
                          >
                            {step.actionLabel}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="dashboard-section" style={{ marginTop: '1rem' }}>
            <div className="section-header" style={{ alignItems: 'center' }}>
              <h3>Fees & settings</h3>
            </div>
            <p className="status-text" style={{ marginTop: '0.5rem' }}>
              Rentual platform fee: {platformFeeType === 'flat' ? formatCurrency(platformFeeValue || 0) : `${Number(platformFeeValue || 0)}%`}
            </p>
            <label className="status-text" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem' }}>
              <input
                type="checkbox"
                checked={!!settings.pass_card_fee_to_tenant}
                onChange={(e) => setSettings({ ...settings, pass_card_fee_to_tenant: e.target.checked })}
              />
              Tenant pays card processing fee
            </label>
            <div style={{ marginTop: '0.75rem' }}>
              <button className="secondary-button" onClick={handleSaveSettings} disabled={saving || !subscriptionActive}>Save</button>
            </div>
            <p className="status-text" style={{ marginTop: '0.75rem' }}>
              Example on {formatCurrency(sampleRent)} rent: tenant pays {formatCurrency(tenantPays)} · landlord receives {formatCurrency(landlordReceives)}
            </p>
          </section>

          <section className="dashboard-section" style={{ marginTop: '1rem' }}>
            <div className="section-header" style={{ alignItems: 'center' }}>
              <h3>Advanced</h3>
              <button className="ghost-button" onClick={() => setShowAdvanced(!showAdvanced)}>
                {showAdvanced ? 'Hide' : 'Show'}
              </button>
            </div>
            {showAdvanced && (
              <>
                <div className="form-actions" style={{ marginTop: '0.5rem' }}>
                  <button className="ghost-button" onClick={handleGenerateLedgers} disabled={!paymentsReady || generatingLedgers}>
                    {generatingLedgers ? 'Generating ledgers…' : 'Generate ledgers for existing tenants'}
                  </button>
                </div>

                <div className="section-header" style={{ alignItems: 'center', marginTop: '1.5rem' }}>
                  <h4>Pre‑authorization</h4>
                </div>
                <label className="status-text" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={!!settings.preauth_enabled}
                    onChange={(e) => setSettings({ ...settings, preauth_enabled: e.target.checked })}
                    disabled={!subscriptionActive}
                  />
                  Enable pre‑authorization (card holds before due date)
                </label>
                <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <label className="status-text">
                    Days before due date
                    <input
                      type="number"
                      className="form-control"
                      value={settings.preauth_days_before ?? 3}
                      min="1"
                      max="7"
                      disabled={!subscriptionActive}
                      onChange={(e) => setSettings({ ...settings, preauth_days_before: e.target.value })}
                      style={{ marginLeft: '0.5rem', maxWidth: '90px' }}
                    />
                  </label>
                  <label className="status-text">
                    Pre‑auth amount (%)
                    <input
                      type="number"
                      className="form-control"
                      value={settings.preauth_amount_pct ?? 100}
                      min="1"
                      max="100"
                      disabled={!subscriptionActive}
                      onChange={(e) => setSettings({ ...settings, preauth_amount_pct: e.target.value })}
                      style={{ marginLeft: '0.5rem', maxWidth: '90px' }}
                    />
                  </label>
                </div>
                <p className="status-text" style={{ marginTop: '0.5rem' }}>
                  Tenants will see a temporary authorization hold. Capture occurs on the due date (or when they press Pay).
                </p>
                <p className="status-text" style={{ marginTop: '0.25rem' }}>
                  Example pre‑auth: {formatCurrency(sampleRent * (Number(settings.preauth_amount_pct || 100) / 100))}
                </p>
              </>
            )}
          </section>

          <section className="card" style={{ marginTop: '1rem', padding: '1rem', border: '1px solid var(--border-color, #e4e4e7)', borderRadius: '12px' }}>
            <h4>Late fees</h4>
            <label className="status-text" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={!!settings.late_fee_enabled}
                onChange={(e) => setSettings({ ...settings, late_fee_enabled: e.target.checked })}
              />
              Enable automatic late fees
            </label>
            {settings.late_fee_enabled && (
              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <label className="status-text">
                    Fee type
                    <select
                      className="form-control"
                      value={settings.late_fee_type || 'fixed'}
                      onChange={(e) => setSettings({ ...settings, late_fee_type: e.target.value })}
                      style={{ marginLeft: '0.5rem', maxWidth: '130px' }}
                    >
                      <option value="fixed">Fixed amount</option>
                      <option value="percent">% of rent</option>
                    </select>
                  </label>
                  <label className="status-text">
                    {settings.late_fee_type === 'percent' ? 'Percentage' : 'Amount ($)'}
                    <input
                      className="form-control"
                      type="number"
                      step={settings.late_fee_type === 'percent' ? '0.1' : '0.01'}
                      min="0"
                      value={settings.late_fee_amount ?? 0}
                      onChange={(e) => setSettings({ ...settings, late_fee_amount: e.target.value })}
                      style={{ marginLeft: '0.5rem', maxWidth: '100px' }}
                    />
                  </label>
                  <label className="status-text">
                    Grace period (days)
                    <input
                      className="form-control"
                      type="number"
                      min="0"
                      max="30"
                      value={settings.late_fee_grace_days ?? 0}
                      onChange={(e) => setSettings({ ...settings, late_fee_grace_days: e.target.value })}
                      style={{ marginLeft: '0.5rem', maxWidth: '80px' }}
                    />
                  </label>
                </div>
                <p className="status-text" style={{ marginTop: '0.5rem' }}>
                  Late fee of {settings.late_fee_type === 'percent' ? `${settings.late_fee_amount || 0}%` : formatCurrency(settings.late_fee_amount || 0)} will be automatically added {Number(settings.late_fee_grace_days || 0) > 0 ? `${settings.late_fee_grace_days} day${settings.late_fee_grace_days > 1 ? 's' : ''} after the due date` : 'on the due date'}.
                </p>
              </div>
            )}
          </section>
        </>
      )}
    </>
  );

  const helpModal = activeHelp ? (
    <div
      className="modal-backdrop"
      onClick={() => setHelpTopic(null)}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.65)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '3rem 1rem',
        overflowY: 'auto'
      }}
    >
      <div
        className="modal-content dashboard-section"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '480px',
          width: '100%',
          background: 'var(--card-bg, #fff)',
          borderRadius: '20px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          padding: '1.5rem',
          position: 'relative'
        }}
      >
        <button
          onClick={() => setHelpTopic(null)}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'none',
            border: 'none',
            fontSize: '1.5rem',
            cursor: 'pointer',
            color: '#94a3b8',
            lineHeight: 1
          }}
          aria-label="Close"
        >
          &times;
        </button>
        <h3 style={{ marginTop: 0, marginBottom: '0.5rem' }}>{activeHelp.title}</h3>
        <p className="status-text" style={{ marginBottom: '1rem' }}>Here’s what to expect:</p>
        <ul style={{ margin: '0 0 1.5rem 0', paddingLeft: '1.25rem' }}>
          {activeHelp.points.map((point, idx) => (
            <li key={idx} style={{ marginBottom: '0.5rem' }} className="status-text">{point}</li>
          ))}
        </ul>
        <div className="form-actions" style={{ justifyContent: 'flex-end' }}>
          <button className="primary-button" onClick={() => setHelpTopic(null)}>Got it</button>
        </div>
      </div>
    </div>
  ) : null;

  if (embedded) {
    return (
      <div className="landlord-settings-embedded">
        {content}
        {helpModal}
      </div>
    );
  }

  return (
    <main className="App-main landlord-subscription-page">
      {content}
      {helpModal}
    </main>
  );
}

export default LandlordSettings;
